import NoTicketsIcon from "@shared/svg/no-tickets.svg?react";
import { AlertBox } from "@shared/ui/AlertBox";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_VARIANTS } from "@shared/ui/Button";
import AppModal from "@shared/ui/Modal";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";
import { Plus } from "phosphor-react";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { ADD_MACHINE_PARTS, ITEMS_PER_PAGE } from "~/constants";
import { useAuth } from "~/components/general";
import { useRoleManager } from "~/hooks/_useRoleManager";
import { useAssignAssetParts } from "~/services/asset";
import { capitalizeFirstLetter } from "~/utils";
import { registerMixpanelEvent } from "~/utils/_mixpanel";

import AddPartModal from "./AddPartModal";
import type { MachinePartsHeadProps } from "./types";
import { UserType } from "@shared/types/user";

const MachinePartsHead = ({
  machineParts = [],
  allParts = [],
  searchQuery,
  setSearchQuery,
  onScrollToBottom,
  partsLoading,
  isDetached,
  totalCount,
  showDetachTemplateAlert,
  setShowDetachTemplateAlert,
  onDeletePartDetachConfirm,
  isSharedAssetsPage = false,
}: MachinePartsHeadProps) => {
  const { messages } = useIntl();
  const { addNewParts: addAssetParts } = useAssignAssetParts();
  const params = useParams<{ id: string }>();

  const [showAddPartModal, setShowAddPartModal] = React.useState(false);
  const [updatingMachineParts, setUpdatingMachineParts] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  const { user } = useAuth() as { user: UserType };
  // @ts-ignore
  const { isOemTechnician } = useRoleManager(user?.role);

  const availableParts = React.useMemo(() => {
    if (!machineParts.length) return allParts;

    const machinePartsIds = new Set(machineParts.map(({ part }) => part._id));
    return allParts.filter((part) => !machinePartsIds.has(part._id));
  }, [allParts, machineParts]);

  const handleAddParts = async () => {
    registerMixpanelEvent(ADD_MACHINE_PARTS);
    try {
      setUpdatingMachineParts(true);
      await addAssetParts({ assetId: params.id, inventoryParts: selectedIds });

      toast(<Toast message={messages?.machines?.["machinePartsAdded"]} />, {
        closeButton: false,
      });
      setSelectedIds([]);
      setShowAddPartModal(false);
    } catch (error: any) {
      toast(
        <Toast
          type={TOAST_TYPES.ERROR}
          message={
            error?.message || messages["hierarchy"]["assets"]["partsAddFailure"]
          }
        />,
        { closeButton: false },
      );
    } finally {
      setUpdatingMachineParts(false);
    }
  };

  useEffect(() => {
    if (
      !partsLoading &&
      availableParts.length < ITEMS_PER_PAGE &&
      totalCount > allParts.length
    ) {
      onScrollToBottom();
    }
  }, [availableParts]);

  useEffect(() => {
    if (showAddPartModal) {
      setSearchQuery("");
    }
  }, [showAddPartModal]);

  return (
    <>
      <section
        className={`flex items-center pb-md border-0 border-b border-solid border-primary ${
          !machineParts.length ? "justify-end" : "justify-between"
        }`}
      >
        {!!machineParts.length && (
          <BodyText
            size={BODY_TEXT_SIZES.X_SMALL}
            color="text-secondary"
          >{`${machineParts.length} ${messages["hierarchy"]["assets"]["totalPartsAdded"]}`}</BodyText>
        )}
        {!isOemTechnician && !isSharedAssetsPage && (
          <Button
            variant={BUTTON_VARIANTS.LINK}
            text={messages?.inventory?.["addNewPart"]}
            leadingIcon={<Plus size={14} weight="bold" />}
            onClick={() => setShowAddPartModal(true)}
          />
        )}
      </section>

      <AppModal
        className="w-[75vw]"
        maxWidth="lg:!max-w-[650px]"
        contentClassName="min-h-[60vh]"
        isOpen={showAddPartModal}
        title={messages["hierarchy"]["assets"]["addParts"] || ""}
        updateButtonText={capitalizeFirstLetter(
          messages?.inventory?.["addParts"] || "",
        )}
        handleClose={() => {
          setShowAddPartModal(false);
          setSelectedIds([]);
        }}
        handleSubmit={handleAddParts}
        disableUpdate={!selectedIds.length || updatingMachineParts}
        disableCancel={updatingMachineParts}
        content={
          <AddPartModal
            allParts={availableParts}
            selectedParts={selectedIds}
            updateSelectedParts={setSelectedIds}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onScrollToBottom={onScrollToBottom}
            partsLoading={partsLoading}
          />
        }
      />

      <AlertBox
        title={
          selectedIds.length
            ? messages["hierarchy"]["assets"]["detachAlerts"]["parts"]["title"]
            : messages["hierarchy"]["assets"]["detachAlerts"]["deleteParts"][
                "title"
              ]
        }
        description={
          selectedIds.length
            ? messages["hierarchy"]["assets"]["detachAlerts"]["parts"][
                "message"
              ]
            : messages["hierarchy"]["assets"]["detachAlerts"]["deleteParts"][
                "message"
              ]
        }
        isOpen={showDetachTemplateAlert}
        cancelButtonText={messages?.common?.["cancel"]}
        acceptButtonText={
          selectedIds.length
            ? messages?.maintenanceDocuments?.["continueButtonText"]
            : messages?.settings?.["companyTabs"].statusCustomization
                .confirmButtonText
        }
        image={<NoTicketsIcon width="130" height="130" />}
        onCancel={() => {
          setShowDetachTemplateAlert(false);
          setShowAddPartModal(false);
          setSelectedIds([]);
        }}
        onAccept={() => {
          setShowDetachTemplateAlert(false);
          if (selectedIds.length) {
            handleAddParts();
          } else {
            onDeletePartDetachConfirm();
          }
        }}
        overlay={true}
      />
    </>
  );
};

export default MachinePartsHead;
